import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import {
	type AddAttendanceForUserFormSchema,
	addAttendanceForUserFormSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { AddAttendanceForm } from "@/forms/add-attendance-form";
import { client } from "@/hono";

/** Props Interface */
interface AddAttendanceModalProps extends ModalProps {
	/** The person whose sheet the entry goes on; without one, the form asks for them. */
	userId?: string;
	onComplete?: () => Promise<unknown>;
}

/**
 * Add an attendance to somebody's sheet after the fact — the day they forgot to check in. The
 * server tops that day's rest up to the legal minimum afterwards, as a check-out would.
 */
export function AddAttendanceModal({
	isOpen,
	onClose,
	userId,
	onComplete,
}: AddAttendanceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(addAttendanceForUserFormSchema),
		defaultValues: {
			userId: userId ?? "",
			date: moment().db(),
			startTime: "",
			endTime: "",
			worksite: "office",
			projectId: null,
		},
	});

	/** Functions */
	async function handleOnSubmit({ userId: personId, ...json }: AddAttendanceForUserFormSchema) {
		const response = await client.users[":userId"].attendances.$post({
			param: { userId: personId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("users.add_attendance")}
				isLoading={methods.formState.isLoading}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<AddAttendanceForm isUserPickable={!userId} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
